import React from 'react';

import { InsightsChartType } from 'services/streamInsightsQuery';

// Small, consistent inline icons — one per InsightsChartType — for the chart-type
// selector. They draw with `currentColor` so the colour follows the surrounding
// text token (no hardcoded colours) and work in light and dark mode.

type IconProps = { size?: number };

const Svg = ({
  size = 16,
  children,
}: IconProps & { children: React.ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    {children}
  </svg>
);

const BarIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <rect x="2" y="2.5" width="9" height="2.2" rx="1" fill="currentColor" />
    <rect x="2" y="6.9" width="12" height="2.2" rx="1" fill="currentColor" />
    <rect x="2" y="11.3" width="6" height="2.2" rx="1" fill="currentColor" />
  </Svg>
);

const ColumnIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <rect x="2.5" y="7" width="2.2" height="7" rx="1" fill="currentColor" />
    <rect x="6.9" y="3" width="2.2" height="11" rx="1" fill="currentColor" />
    <rect x="11.3" y="9" width="2.2" height="5" rx="1" fill="currentColor" />
  </Svg>
);

const LineIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <polyline
      points="2,11 6,6 9,9 14,3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const AreaIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <path
      d="M2 11 L6 6 L9 9 L14 3 V14 H2 Z"
      fill="currentColor"
      fillOpacity="0.35"
    />
    <polyline
      points="2,11 6,6 9,9 14,3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const PieIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <circle cx="8" cy="8" r="6" fill="currentColor" fillOpacity="0.35" />
    <path d="M8 8 V2 A6 6 0 0 1 13.2 5 Z" fill="currentColor" />
  </Svg>
);

const DonutIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <path
      d="M8 2 A6 6 0 1 1 2 8"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
    <path
      d="M2 8 A6 6 0 0 1 8 2"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeOpacity="0.4"
    />
  </Svg>
);

const RadarIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <polygon
      points="8,1.5 14,6 11.7,13.5 4.3,13.5 2,6"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <polygon
      points="8,5 11,7.2 10,11 6,11 5,7.2"
      fill="currentColor"
      fillOpacity="0.35"
    />
  </Svg>
);

const WaterfallIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <rect x="1.5" y="9" width="2.6" height="3" rx="0.6" fill="currentColor" />
    <rect x="5" y="6" width="2.6" height="3" rx="0.6" fill="currentColor" />
    <rect x="8.5" y="4" width="2.6" height="3" rx="0.6" fill="currentColor" />
    <rect x="12" y="2" width="2.6" height="3" rx="0.6" fill="currentColor" />
  </Svg>
);

const PackedBubbleIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <circle cx="6" cy="7" r="4" fill="currentColor" fillOpacity="0.4" />
    <circle cx="11.5" cy="5" r="2.5" fill="currentColor" fillOpacity="0.6" />
    <circle cx="11" cy="10.5" r="3" fill="currentColor" />
  </Svg>
);

const FunnelIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <rect x="2" y="2.5" width="12" height="2.2" rx="0.6" fill="currentColor" />
    <rect x="3.5" y="5.6" width="9" height="2.2" rx="0.6" fill="currentColor" />
    <rect x="5" y="8.7" width="6" height="2.2" rx="0.6" fill="currentColor" />
    <rect
      x="6.5"
      y="11.8"
      width="3"
      height="2.2"
      rx="0.6"
      fill="currentColor"
    />
  </Svg>
);

const PyramidIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <path d="M8 2 L10 6 H6 Z" fill="currentColor" />
    <path
      d="M6 6.6 L10 6.6 L11.5 10 H4.5 Z"
      fill="currentColor"
      fillOpacity="0.7"
    />
    <path
      d="M4.5 10.6 L11.5 10.6 L13.5 14 H2.5 Z"
      fill="currentColor"
      fillOpacity="0.4"
    />
  </Svg>
);

const ScatterIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <circle cx="4" cy="11" r="1.6" fill="currentColor" />
    <circle cx="7.5" cy="6.5" r="1.6" fill="currentColor" />
    <circle cx="11" cy="9" r="1.6" fill="currentColor" />
    <circle cx="12.5" cy="4" r="1.6" fill="currentColor" />
  </Svg>
);

const BubbleIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <circle cx="4.5" cy="10.5" r="2.4" fill="currentColor" fillOpacity="0.6" />
    <circle cx="10.5" cy="6" r="3.4" fill="currentColor" fillOpacity="0.6" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" fillOpacity="0.6" />
  </Svg>
);

const HeatmapIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <rect
      x="2"
      y="2"
      width="4"
      height="4"
      rx="0.6"
      fill="currentColor"
      fillOpacity="0.3"
    />
    <rect
      x="6.5"
      y="2"
      width="4"
      height="4"
      rx="0.6"
      fill="currentColor"
      fillOpacity="0.7"
    />
    <rect
      x="11"
      y="2"
      width="3"
      height="4"
      rx="0.6"
      fill="currentColor"
      fillOpacity="0.5"
    />
    <rect
      x="2"
      y="6.5"
      width="4"
      height="4"
      rx="0.6"
      fill="currentColor"
      fillOpacity="0.9"
    />
    <rect
      x="6.5"
      y="6.5"
      width="4"
      height="4"
      rx="0.6"
      fill="currentColor"
      fillOpacity="0.4"
    />
    <rect
      x="11"
      y="6.5"
      width="3"
      height="4"
      rx="0.6"
      fill="currentColor"
      fillOpacity="0.7"
    />
    <rect
      x="2"
      y="11"
      width="4"
      height="3"
      rx="0.6"
      fill="currentColor"
      fillOpacity="0.5"
    />
    <rect
      x="6.5"
      y="11"
      width="4"
      height="3"
      rx="0.6"
      fill="currentColor"
      fillOpacity="0.8"
    />
    <rect
      x="11"
      y="11"
      width="3"
      height="3"
      rx="0.6"
      fill="currentColor"
      fillOpacity="0.3"
    />
  </Svg>
);

const SankeyIcon = ({ size }: IconProps) => (
  <Svg size={size}>
    <rect x="1.5" y="3" width="2" height="10" rx="0.6" fill="currentColor" />
    <rect x="12.5" y="2.5" width="2" height="5" rx="0.6" fill="currentColor" />
    <rect x="12.5" y="9" width="2" height="4.5" rx="0.6" fill="currentColor" />
    <path
      d="M3.5 5 C8 5 8 5 12.5 4"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeOpacity="0.4"
      fill="none"
    />
    <path
      d="M3.5 10 C8 10 8 11 12.5 11"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeOpacity="0.4"
      fill="none"
    />
  </Svg>
);

export const CHART_TYPE_ICONS: Record<
  InsightsChartType,
  React.FC<IconProps>
> = {
  bar: BarIcon,
  column: ColumnIcon,
  line: LineIcon,
  area: AreaIcon,
  pie: PieIcon,
  donut: DonutIcon,
  radar: RadarIcon,
  waterfall: WaterfallIcon,
  packedBubble: PackedBubbleIcon,
  funnel: FunnelIcon,
  pyramid: PyramidIcon,
  scatter: ScatterIcon,
  bubble: BubbleIcon,
  heatmap: HeatmapIcon,
  sankey: SankeyIcon,
};

export default CHART_TYPE_ICONS;
